import { useMutation, useQueryClient } from "@tanstack/react-query";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { displayErrorToast, displayToast } from "@/utils/toast";

export interface ArchivePromptVersionRequest {
  prompt_version_id: number;
  restore?: boolean;
}

export interface ArchivePromptVersionResponse {
  success: boolean;
  message: string;
}

export const useArchivePromptVersion = () => {
  const queryClient = useQueryClient();
  const authContext = useAuth();

  return useMutation<ArchivePromptVersionResponse, Error, ArchivePromptVersionRequest>({
    mutationFn: async ({ prompt_version_id, restore }) => {
      const response = await fetch(`${API_URL}/archive-prompt-version`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authContext?.userToken}`,
        },
        credentials: "include",
        body: JSON.stringify({
          prompt_version_id,
          restore,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `Failed to ${restore ? "restore" : "hide"} prompt version`,
        );
      }

      return response.json();
    },
    onSuccess: async (data, v) => {
      displayToast(`Version ${v.restore ? "restored" : "hidden"} successfully`, ToastType.success);

      // Invalidate all prompt version queries
      // The usePromptVersions query key structure is: [url.toString(), userToken, JSON.stringify(params)]
      // We need to match queries that contain the prompt_versions endpoint
      await queryClient.invalidateQueries({
        predicate: query => {
          const keyString = JSON.stringify(query.queryKey);
          return keyString.includes("prompt-versions");
        },
      });

      // Force refetch all active queries to ensure UI updates immediately
      await queryClient.refetchQueries({
        predicate: query => {
          const keyString = JSON.stringify(query.queryKey);
          return keyString.includes("prompt-versions");
        },
        type: "active",
      });
    },
    onError: (error, v) => {
      displayErrorToast(
        error.message || `Failed to ${v.restore ? "restore" : "hide"} prompt version`,
      );
    },
  });
};
